import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Pizza, CheckCircle, XCircle, HelpCircle, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface PizzaFractionGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Challenge {
  type: 'create' | 'identify' | 'compare';
  targetFraction: { numerator: number; denominator: number };
  question: string;
  options?: { numerator: number; denominator: number }[];
}

const PizzaFractionGame: React.FC<PizzaFractionGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [selectedSlices, setSelectedSlices] = useState<boolean[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [challengeCount, setChallengeCount] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to Pizza Fractions!' : 'Bienvenue dans les Fractions Pizza !',
      content: language === 'en' 
        ? 'Learn fractions by cutting and sharing pizzas! Click slices to select them.'
        : 'Apprenez les fractions en coupant et partageant des pizzas ! Cliquez sur les parts pour les sélectionner.'
    },
    {
      title: language === 'en' ? 'Understanding Fractions' : 'Comprendre les fractions',
      content: language === 'en'
        ? 'A fraction shows parts of a whole. 1/4 means 1 part out of 4 equal parts.'
        : 'Une fraction montre des parties d\'un tout. 1/4 signifie 1 partie sur 4 parties égales.'
    },
    {
      title: language === 'en' ? 'Ready to Start!' : 'Prêt à commencer !',
      content: language === 'en'
        ? 'Try to create the fraction shown by selecting the correct pizza slices!'
        : 'Essayez de créer la fraction montrée en sélectionnant les bonnes parts de pizza !'
    }
  ];

  const generateChallenge = (): Challenge => {
    const challengeTypes = level === 1 ? ['create', 'identify'] : 
                          level === 2 ? ['create', 'identify', 'compare'] : 
                          ['create', 'identify', 'compare'];
    
    const type = challengeTypes[Math.floor(Math.random() * challengeTypes.length)] as Challenge['type'];
    
    let denominators: number[];
    if (level === 1) {
      denominators = [2, 4, 8];
    } else if (level === 2) {
      denominators = [3, 4, 6, 8];
    } else {
      denominators = [3, 4, 5, 6, 8, 10];
    }
    
    const denominator = denominators[Math.floor(Math.random() * denominators.length)];
    const numerator = Math.floor(Math.random() * denominator) + 1;
    
    switch (type) {
      case 'create':
        return {
          type,
          targetFraction: { numerator, denominator },
          question: language === 'en'
            ? `Select ${numerator}/${denominator} of the pizza`
            : `Sélectionnez ${numerator}/${denominator} de la pizza`
        };
        
      case 'identify':
        return {
          type,
          targetFraction: { numerator, denominator },
          question: language === 'en'
            ? 'What fraction is selected?'
            : 'Quelle fraction est sélectionnée ?',
          options: generateFractionOptions({ numerator, denominator })
        };
        
      case 'compare':
        const fraction2 = generateComparableFraction({ numerator, denominator });
        return {
          type,
          targetFraction: { numerator, denominator },
          question: language === 'en'
            ? `Which is larger: ${numerator}/${denominator} or ${fraction2.numerator}/${fraction2.denominator}?`
            : `Lequel est plus grand : ${numerator}/${denominator} ou ${fraction2.numerator}/${fraction2.denominator} ?`,
          options: [
            { numerator, denominator },
            fraction2,
            { numerator: 0, denominator: 1 } // "Equal" option
          ]
        };
        
      default:
        return generateChallenge();
    }
  };

  const generateFractionOptions = (correct: { numerator: number; denominator: number }) => {
    const options = [correct];
    
    while (options.length < 4) {
      const denominator = [2, 3, 4, 6, 8][Math.floor(Math.random() * 5)];
      const numerator = Math.floor(Math.random() * denominator) + 1;
      const newOption = { numerator, denominator };
      
      if (!options.some(opt => opt.numerator === numerator && opt.denominator === denominator)) {
        options.push(newOption);
      }
    }
    
    return options.sort(() => Math.random() - 0.5);
  };

  const generateComparableFraction = (fraction1: { numerator: number; denominator: number }) => {
    const value1 = fraction1.numerator / fraction1.denominator;
    let fraction2;
    
    do {
      const denominator = [2, 3, 4, 6, 8][Math.floor(Math.random() * 5)];
      const numerator = Math.floor(Math.random() * denominator) + 1;
      fraction2 = { numerator, denominator };
    } while (Math.abs((fraction2.numerator / fraction2.denominator) - value1) < 0.1);
    
    return fraction2;
  };

  const drawPizza = () => {
    const canvas = canvasRef.current;
    if (!canvas || !currentChallenge) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 120;
    const slices = currentChallenge.targetFraction.denominator;
    
    // Draw pizza base
    ctx.fillStyle = '#F59E0B';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw pizza crust
    ctx.strokeStyle = '#92400E';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    // Draw slice lines and handle selection
    for (let i = 0; i < slices; i++) {
      const angle = (2 * Math.PI * i) / slices - Math.PI / 2;
      
      // Draw slice line
      ctx.strokeStyle = '#92400E';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(angle),
        centerY + radius * Math.sin(angle)
      );
      ctx.stroke();
      
      // Fill selected slices
      if (selectedSlices[i]) {
        ctx.fillStyle = '#EF4444';
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius - 2, angle, angle + (2 * Math.PI) / slices);
        ctx.closePath();
        ctx.fill();
      }
      
      // Add toppings to selected slices
      if (selectedSlices[i]) {
        const midAngle = angle + Math.PI / slices;
        const toppingRadius = radius * 0.6;
        
        // Pepperoni
        ctx.fillStyle = '#DC2626';
        ctx.beginPath();
        ctx.arc(
          centerX + toppingRadius * Math.cos(midAngle) * 0.7,
          centerY + toppingRadius * Math.sin(midAngle) * 0.7,
          8,
          0,
          2 * Math.PI
        );
        ctx.fill();
      }
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!currentChallenge || showFeedback) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 120;
    
    // Calculate distance from center
    const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
    if (distance > radius) return;
    
    // Calculate angle
    const angle = Math.atan2(y - centerY, x - centerX) + Math.PI / 2;
    const normalizedAngle = angle < 0 ? angle + 2 * Math.PI : angle;
    
    // Determine which slice was clicked
    const sliceIndex = Math.floor((normalizedAngle / (2 * Math.PI)) * currentChallenge.targetFraction.denominator);
    
    // Toggle slice selection
    const newSelection = [...selectedSlices];
    newSelection[sliceIndex] = !newSelection[sliceIndex];
    setSelectedSlices(newSelection);
  };

  const checkAnswer = (selectedOption?: { numerator: number; denominator: number }) => {
    if (!currentChallenge) return;
    
    let correct = false;
    
    if (currentChallenge.type === 'create') {
      const selectedCount = selectedSlices.filter(Boolean).length;
      correct = selectedCount === currentChallenge.targetFraction.numerator;
    } else if (currentChallenge.type === 'identify' && selectedOption) {
      const selectedCount = selectedSlices.filter(Boolean).length;
      const actualFraction = { 
        numerator: selectedCount, 
        denominator: currentChallenge.targetFraction.denominator 
      };
      correct = selectedOption.numerator === actualFraction.numerator && 
                selectedOption.denominator === actualFraction.denominator;
    } else if (currentChallenge.type === 'compare' && selectedOption) {
      const value1 = currentChallenge.targetFraction.numerator / currentChallenge.targetFraction.denominator;
      const value2 = currentChallenge.options![1].numerator / currentChallenge.options![1].denominator;
      
      if (Math.abs(value1 - value2) < 0.001) {
        correct = selectedOption.numerator === 0; // Equal option
      } else if (value1 > value2) {
        correct = selectedOption.numerator === currentChallenge.targetFraction.numerator &&
                 selectedOption.denominator === currentChallenge.targetFraction.denominator;
      } else {
        correct = selectedOption.numerator === currentChallenge.options![1].numerator &&
                 selectedOption.denominator === currentChallenge.options![1].denominator;
      }
    }
    
    setIsCorrect(correct);
    setShowFeedback(true);
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
    
    if (correct) {
      setScore(score + 1);
      playSuccess();
    } else {
      playError();
    }
    
    setTimeout(() => {
      setChallengeCount(challengeCount + 1);
      if (challengeCount >= 9) {
        setGameComplete(true);
        saveProgress(newStats);
      } else {
        nextChallenge();
      }
    }, 2000);
  };

  const nextChallenge = () => {
    setCurrentChallenge(generateChallenge());
    setSelectedSlices([]);
    setShowFeedback(false);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'fractions',
        mode: 'pizza',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setChallengeCount(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    nextChallenge();
  };

  useEffect(() => {
    if (!showTutorial && currentChallenge) {
      drawPizza();
    }
  }, [currentChallenge, selectedSlices, showTutorial]);

  useEffect(() => {
    if (!showTutorial) {
      nextChallenge();
    }
  }, [showTutorial]);

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Pizza className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Start Playing!' : 'Commencer à jouer !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <select
            value={level}
            onChange={(e) => setLevel(parseInt(e.target.value))}
            className="px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg"
          >
            <option value={1}>{language === 'en' ? 'Level 1: Simple Fractions' : 'Niveau 1 : Fractions simples'}</option>
            <option value={2}>{language === 'en' ? 'Level 2: Mixed Fractions' : 'Niveau 2 : Fractions mixtes'}</option>
            <option value={3}>{language === 'en' ? 'Level 3: Comparisons' : 'Niveau 3 : Comparaisons'}</option>
          </select>
          
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Pizza Master!' : 'Maître des Pizzas !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{score}/10</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Score' : 'Score'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                {Math.round((score / 10) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Pizza Canvas */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-6 text-center">
              {language === 'en' ? 'Pizza Fractions' : 'Fractions Pizza'}
            </h2>
            
            <div className="flex justify-center mb-6">
              <canvas
                ref={canvasRef}
                width={300}
                height={300}
                onClick={handleCanvasClick}
                className="border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer"
              />
            </div>
            
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              {language === 'en' ? 'Click on pizza slices to select them' : 'Cliquez sur les parts de pizza pour les sélectionner'}
            </div>
          </div>

          {/* Challenge Panel */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
                <Pizza className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  {language === 'en' ? 'Challenge' : 'Défi'} {challengeCount + 1}/10
                </h3>
              </div>
              
              <p className="text-slate-700 dark:text-slate-300 mb-6 text-lg">
                {currentChallenge?.question}
              </p>

              {currentChallenge?.type === 'create' && (
                <button
                  onClick={() => checkAnswer()}
                  disabled={showFeedback}
                  className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {language === 'en' ? 'Check Answer' : 'Vérifier'}
                </button>
              )}

              {currentChallenge?.options && (
                <div className="grid grid-cols-2 gap-3">
                  {currentChallenge.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => checkAnswer(option)}
                      disabled={showFeedback}
                      className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-50"
                    >
                      {option.numerator === 0 ? 
                        (language === 'en' ? 'Equal' : 'Égal') : 
                        `${option.numerator}/${option.denominator}`}
                    </button>
                  ))}
                </div>
              )}

              {showFeedback && (
                <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 ${
                  isCorrect 
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                }`}>
                  {isCorrect ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                  <span className="font-medium">
                    {isCorrect 
                      ? (language === 'en' ? 'Correct!' : 'Correct !')
                      : (language === 'en' ? 'Try again next time!' : 'Réessayez la prochaine fois !')}
                  </span>
                </div>
              )}

              <div className="mt-6 text-sm text-slate-500 dark:text-slate-400">
                {language === 'en' ? 'Score:' : 'Score :'} {score}/{challengeCount + 1}
              </div>
            </div>

            {/* Fraction Help */}
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                {language === 'en' ? 'Fraction Help:' : 'Aide sur les fractions :'}
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {language === 'en' ? 'Top number = parts selected' : 'Nombre du haut = parties sélectionnées'}</li>
                <li>• {language === 'en' ? 'Bottom number = total parts' : 'Nombre du bas = parties totales'}</li>
                <li>• {language === 'en' ? '1/2 = half of the pizza' : '1/2 = la moitié de la pizza'}</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PizzaFractionGame;